import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categoryService } from '../services/api';
import { getCategorySlug, sortMainCategoriesForDisplay } from '../utils/categoryUtils';
import SiteLoading from '../components/SiteLoading';
import { placeholderDataUrl } from '../utils/placeholderImage';
import './AllCategoriesPage.css';

const AllCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Store all categories for hierarchical slug generation
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 AllCategoriesPage: Loading categories from API...');
        
        // Fetch all categories from API
        const response = await categoryService.getCategories();
        
        let categoriesData = [];
        
        if (response.success && response.categories) {
          if (Array.isArray(response.categories)) {
            categoriesData = response.categories;
          } else if (Array.isArray(response.categories.data)) {
            categoriesData = response.categories.data;
          }
        } else if (Array.isArray(response)) {
          categoriesData = response;
        }
        
        console.log('✅ AllCategoriesPage: Categories loaded:', categoriesData.length);
        
        // Store all categories for hierarchical slug generation
        setAllCategories(categoriesData);
        
        // Filter only active categories
        const activeCategories = categoriesData.filter(category => {
          return category.isActive !== false;
        });
        
        // Separate main categories and subcategories.
        // Main categories are displayed in the same fixed order as the header / hero / mobile drawer:
        // Women → Men → Kids → Accessories → Fragrances → Shoes (others alphabetical after).
        const mainCategories = sortMainCategoriesForDisplay(
          activeCategories.filter(category => {
            const parentId = category.parentCategory?._id || category.parentCategory || category.parent;
            return !parentId || parentId === '' || parentId === null;
          })
        );

        // Get subcategories for each main category
        const categoriesWithSubcategories = mainCategories.map(mainCategory => {
          const mainCategoryId = mainCategory._id || mainCategory.id;
          const subcategories = activeCategories.filter(category => {
            const parentId = category.parentCategory?._id || category.parentCategory || category.parent;
            if (!parentId) return false;
            const parentIdString = String(parentId);
            const mainIdString = String(mainCategoryId);
            return parentIdString === mainIdString || parentId === mainCategoryId;
          });
          
          return {
            ...mainCategory,
            subcategories: subcategories,
            isMainCategory: true
          };
        });
        
        // Get all subcategories separately
        const allSubcategories = activeCategories.filter(category => {
          const parentId = category.parentCategory?._id || category.parentCategory || category.parent;
          return parentId && parentId !== '' && parentId !== null;
        }).map(subcategory => ({
          ...subcategory,
          isMainCategory: false
        }));
        
        // Combine main categories and subcategories for display
        const allCategoriesForDisplay = [
          ...categoriesWithSubcategories,
          ...allSubcategories
        ];
        
        setCategories(allCategoriesForDisplay);
        console.log('✅ AllCategoriesPage: Main categories:', categoriesWithSubcategories.length);
        console.log('✅ AllCategoriesPage: Subcategories:', allSubcategories.length);
        console.log('✅ AllCategoriesPage: Total categories for display:', allCategoriesForDisplay.length);
      } catch (err) {
        console.error('❌ AllCategoriesPage: Error loading categories:', err);
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadCategories();
  }, []);

  // Helper function to create category slug (now returns hierarchical path)
  const getCategorySlugLocal = (category) => {
    return getCategorySlug(category, allCategories);
  };

  if (loading) {
    return (
      <div className="all-categories-page">
        <div className="all-categories-container">
          <SiteLoading
            title="Loading Categories"
            subtitle="Fetching categories from server..."
            icon="📂"
            variant="full"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="all-categories-page">
        <div className="all-categories-container">
          <div className="page-header">
            <div className="header-content">
              <h1 className="page-title">All Categories</h1>
              <p className="page-subtitle" style={{ color: '#dc3545' }}>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="all-categories-page">
        <div className="all-categories-container">
          <div className="page-header">
            <div className="header-content">
              <h1 className="page-title">All Categories</h1>
              <p className="page-subtitle">No categories available at the moment.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="all-categories-page">
      <div className="all-categories-container">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">
              <span>Browse Collection</span>
              <span className="page-title-text">All Categories</span>
            </h1>
            <p className="page-subtitle">Explore our complete collection of fashion categories</p>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="categories-section">
          <div className="categories-grid">
            {categories.map(category => {
              const categoryId = category._id || category.id;
              const categorySlug = getCategorySlugLocal(category);
              const categoryLink = `/category/${categorySlug}`;
              const isMainCategory = category.isMainCategory === true;
              
              return (
                <div 
                  key={categoryId} 
                  className={`category-item ${isMainCategory ? 'main-category' : 'subcategory-item'}`}
                >
                  <Link to={categoryLink} className="category-link">
                    <div className="category-image">
                      <img 
                        src={category.image || placeholderDataUrl(400, 300, category.name)} 
                        alt={category.name}
                        onError={(e) => {
                          e.target.src = placeholderDataUrl(400, 300, category.name);
                        }}
                      />
                      <div className="category-overlay">
                        <div className={`category-badge ${isMainCategory ? 'main-badge' : 'sub-badge'}`}>
                          <span>{isMainCategory ? 'Main' : 'Sub'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="category-content">
                      <h3 className="category-name">
                        {category.name}
                        {!isMainCategory && (
                          <span className="subcategory-indicator">Subcategory</span>
                        )}
                      </h3>
                      <p className="category-description">
                        {category.description || `Explore our ${category.name} collection`}
                      </p>
                      
                      {category.subcategories && category.subcategories.length > 0 && (
                        <div className="category-details">
                          <div className="subcategories">
                            <h4>Subcategories:</h4>
                            <div className="subcategories-list">
                              {category.subcategories.slice(0, 3).map((sub, index) => {
                                const subId = sub._id || sub.id || index;
                                return (
                                  <span key={subId} className="subcategory-tag">
                                    {sub.name}
                                  </span>
                                );
                              })}
                              {category.subcategories.length > 3 && (
                                <span className="subcategory-more">
                                  +{category.subcategories.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="category-action">
                        <span className="explore-btn">
                          Explore {category.name}
                          <i className="fas fa-arrow-right"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="quick-navigation">
          <h3>Quick Navigation</h3>
          <div className="nav-links">
            <Link to="/" className="nav-link">
              <i className="fas fa-home"></i>
              Back to Home
            </Link>
            <Link to="/category/new-in" className="nav-link">
              <i className="fas fa-star"></i>
              New Arrivals
            </Link>
            <Link to="/category/ready-to-wear" className="nav-link">
              <i className="fas fa-tshirt"></i>
              Ready to Wear
            </Link>
            <Link to="/category/unstitched" className="nav-link">
              <i className="fas fa-cut"></i>
              Unstitched
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCategoriesPage; 